import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "./Header/Header";
import ListMenu from "./ListMenu/ListMenu";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import useSize from "../../../../Utils/useSize/useSize";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import SummaryCard from "./SummaryCard";
import Total from "./Total/Total";

import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = require("indian-number-format");
const backend = await new Backend().init();

export default function Summary({ master_id, userType }) {
  const windowSize = useSize();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState([]);
  const [summaryData, setSummaryData] = useState({});

  // ✅ FIXED: Proper ternary logic
  const loadSummary = useCallback(async () => {
    setLoading(true);

    const base = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    };

    const data =
      userType === 3
        ? { ...base, master_id: localStorage.getItem("id") }
        : userType === 2
        ? master_id
          ? { ...base, master_id }
          : { ...base, super_id: localStorage.getItem("id") }
        : master_id
        ? { ...base, master_id }
        : { ...base, admin_id: localStorage.getItem("id") };

    try {
      const response = await backend.load_summary_broker(data);
      setLoading(false);

      if (response.error === "False") {
        setUser(response.broker || []);
      } else if (response.logout === 1) {
        window.LogOut?.();
      } else {
        toast.error(response.message || "Failed to load summary");
      }
    } catch (err) {
      console.error("Error loading summary:", err);
      setLoading(false);
      toast.error("Server error. Please try again later.");
    }
  }, [master_id, userType]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleSummaryCalculated = (data) => {
    setSummaryData((prev) => ({
      ...prev,
      [data.user_id]: data,
    }));
  };

  // ✅ Fixed useMemo
  const totalSummary = useMemo(() => {
    const all = Object.values(summaryData);
    if (all.length === 0) return null;

    const sum = (key) =>
      all.reduce((acc, curr) => acc + parseFloat(curr[key] || 0), 0);

    return {
      booked: sum("booked"),
      deposit_total: sum("deposit_total"),
      deposit_current: sum("deposit_current"),
      upper: sum("upper"),
      self: sum("self"),
      lower: sum("lower"),
      upperLive: sum("upperLive"),
      selfLive: sum("selfLive"),
      lowerLive: sum("lowerLive"),
      total: sum("total"),
    };
  }, [summaryData]);

  // ✅ Fixed Excel export logic
  const downloadExcel = () => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      toast.error("No data to export");
      return;
    }

    const data = Object.values(summaryData).map((item) => ({
      ...(userType === 1 && { Super: item.super_username }),
      ...((userType === 1 || userType === 2) && {
        Master: item.master_username,
      }),
      Username: item.username,
      Booked: item.booked,
      BTotal: item.deposit_total,
      BCurrent: item.deposit_current,
      "Upper Share": item.upper,
      "Self Share": item.self,
      "Lower Share": item.lower,
      "Upper Live": item.upperLive,
      "Self Live": item.selfLive,
      "Lower Live": item.lowerLive,
      "Live Total": item.total,
    }));

    if (totalSummary) {
      data.push({
        ...(userType === 1 && { Super: "TOTAL" }),
        ...((userType === 1 || userType === 2) && { Master: "TOTAL" }),
        Username: "TOTAL",
        Booked: totalSummary.booked,
        BTotal: totalSummary.deposit_total,
        BCurrent: totalSummary.deposit_current,
        "Upper Share": totalSummary.upper,
        "Self Share": totalSummary.self,
        "Lower Share": totalSummary.lower,
        "Upper Live": totalSummary.upperLive,
        "Self Live": totalSummary.selfLive,
        "Lower Live": totalSummary.lowerLive,
        "Live Total": totalSummary.total,
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `User-Summary-${moment().format("DD-MMM-YYYY")}.xlsx`);
  };

  const downloadPDF = useCallback(async () => {
    try {
      const pdfMakeModule = await import("pdfmake/build/pdfmake");
      const pdfFontsModule = await import("pdfmake/build/vfs_fonts");

      const pdfMake = pdfMakeModule.default || pdfMakeModule;
      const pdfFonts = pdfFontsModule.default || pdfFontsModule;
      pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

      const rows = Object.values(summaryData || []);
      if (rows.length === 0) {
        toast.error("No data available to export");
        return;
      }

      // Build headers depending on userType
      const headers = [
        ...(userType === 1 ? [{ text: "Super", style: "tableHeader" }] : []),
        ...(userType === 1 || userType === 2
          ? [{ text: "Master", style: "tableHeader" }]
          : []),
        { text: "Username", style: "tableHeader" },
        { text: "Booked", style: "tableHeader" },
        { text: "Credit", style: "tableHeader" },
        { text: "Current", style: "tableHeader" },
        { text: "Super Sharing", style: "tableHeader" },
        { text: "Master Sharing", style: "tableHeader" },
        { text: "Broker Sharing", style: "tableHeader" },
        { text: "Super Live P&L", style: "tableHeader" },
        { text: "Master Live P&L", style: "tableHeader" },
        { text: "Broker Live P&L", style: "tableHeader" },
        { text: "Live P&L", style: "tableHeader" },
      ];

      // Build body rows
      const body = [
        headers,
        ...rows.map((i) => [
          ...(userType === 1
            ? [{ text: i.super_username ?? "-", style: "tableCell" }]
            : []),
          ...(userType === 1 || userType === 2
            ? [{ text: i.master_username ?? "-", style: "tableCell" }]
            : []),
          { text: i.username ?? "-", style: "tableCell" },
          {
            text: fmt.format(i.booked ?? 0),
            style: "numericCell",
            color: parseFloat(i.booked || 0) > 0 ? "#399918" : "#FF0000",
          },
          { text: fmt.format(i.deposit_total ?? 0), style: "numericCell" },
          { text: fmt.format(i.deposit_current ?? 0), style: "numericCell" },
          {
            text: fmt.format(i.upper ?? 0),
            style: "numericCell",
            color:
              parseFloat(i.upper || 0) > parseFloat(i.total || 0)
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(i.self ?? 0),
            style: "numericCell",
            color:
              parseFloat(i.self || 0) > parseFloat(i.total || 0)
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(i.lower ?? 0),
            style: "numericCell",
            color:
              parseFloat(i.lower || 0) > parseFloat(i.total || 0)
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(i.upperLive ?? 0),
            style: "numericCell",
            color: parseFloat(i.total || 0) > 0 ? "#FF0000" : "#399918",
          },
          {
            text: fmt.format(i.selfLive ?? 0),
            style: "numericCell",
            color: parseFloat(i.total || 0) > 0 ? "#FF0000" : "#399918",
          },
          {
            text: fmt.format(i.lowerLive ?? 0),
            style: "numericCell",
            color: parseFloat(i.total || 0) > 0 ? "#FF0000" : "#399918",
          },
          {
            text: fmt.format(i.total ?? 0),
            style: "numericCell",
            color: parseFloat(i.total || 0) > 0 ? "#399918" : "#FF0000",
          },
        ]),
      ];

      // Append totals row if available
      if (totalSummary) {
        body.push([
          ...(userType === 1
            ? [{ text: "TOTAL", style: "tableCell", color: "#0182f7" }]
            : []),
          ...(userType === 1 || userType === 2
            ? [{ text: "MASTER", style: "tableCell", color: "#0182f7" }]
            : []),
          { text: "USER", style: "tableCell", color: "#0182f7" },
          {
            text: fmt.format(totalSummary.booked ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.booked || 0) > 0 ? "#399918" : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.deposit_total ?? 0),
            style: "numericCell",
          },
          {
            text: fmt.format(totalSummary.deposit_current ?? 0),
            style: "numericCell",
          },
          {
            text: fmt.format(totalSummary.upper ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.upper || 0) > 0 ? "#399918" : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.self ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.self || 0) > 0 ? "#399918" : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.lower ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.lower || 0) > 0 ? "#399918" : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.upperLive ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.upperLive || 0) > 0
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.selfLive ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.selfLive || 0) > 0
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.lowerLive ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.lowerLive || 0) > 0
                ? "#399918"
                : "#FF0000",
          },
          {
            text: fmt.format(totalSummary.total ?? 0),
            style: "numericCell",
            color:
              parseFloat(totalSummary.total || 0) > 0 ? "#399918" : "#FF0000",
          },
        ]);
      }

      const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        pageMargins: [40, 60, 40, 60],
        content: [
          {
            columns: [
              { image: "logo", width: 60 },
              {
                text: `${window.APP_NAME} USER REPORT`,
                style: "header",
                width: "*",
                alignment: "right",
              },
            ],
            columnGap: 10,
          },
          {
            text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
            style: "subHeader",
          },
          {
            text: "------------------------------------------------------------",
            margin: [0, 5, 0, 5],
          },
          {
            table: {
              headerRows: 1,
              widths: headers.map(() => "auto"),
              body,
            },
            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0
                  ? "#1E293B"
                  : rowIndex % 2 === 0
                  ? "#F8FAFC"
                  : null,
              hLineColor: "#CBD5E1",
              vLineColor: "#CBD5E1",
            },
          },
          {
            text: `\nThank you for using ${window.APP_NAME}.`,
            style: "footerNote",
          },
        ],
        images: { logo: window.LOGO },
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            color: "#1E293B",
            margin: [0, 0, 0, 10],
          },
          subHeader: { fontSize: 10, color: "#64748B", margin: [0, 0, 0, 10] },
          tableHeader: {
            bold: true,
            fontSize: 11,
            color: "#FFFFFF",
            fillColor: "#1E293B",
            alignment: "center",
          },
          tableCell: { fontSize: 10, margin: [0, 5, 0, 5] },
          numericCell: {
            fontSize: 10,
            margin: [0, 5, 0, 5],
            alignment: "right",
          },
          footerNote: {
            fontSize: 9,
            color: "#475569",
            italics: true,
            margin: [0, 15, 0, 0],
            alignment: "center",
          },
        },
        footer: (currentPage, pageCount) => ({
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "center",
          fontSize: 9,
          color: "#64748B",
        }),
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`${moment().format("DD-MMM")}-User-Summary.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  }, [summaryData, totalSummary, userType]);

  return (
    <div>
      <Header
        records={user.length}
        downloadPDF={downloadPDF}
        downloadExcel={downloadExcel}
      />

      {/* ✅ Pass userType to ListMenu */}
      <ListMenu userType={userType} />

      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 240,
          overflowY: "scroll",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 240} />
          </div>
        ) : user.length > 0 ? (
          user.map((item, index) => (
            <SummaryCard
              key={item.id || index}
              item={item}
              index={index}
              handleSummaryCalculated={handleSummaryCalculated}
              userType={userType}
            />
          ))
        ) : (
          <NotFound
            height={windowSize[1] - 240}
            title="Nothing here"
            subtext="No Broker Summary Found."
          />
        )}
      </div>

      <Total userType={userType} totalSummary={totalSummary} />
    </div>
  );
}
